// /live-uploading, Spanish. Same keys and facts as copy.en.ts (source of truth). Labels in «» are CloudTether's and
// Photohawk's own, in English, as they show them.
import type { LiveUploadingCopy } from "./copy.en";

const es: LiveUploadingCopy = {
  seo: {
    title: "Sube fotos en directo con CloudTether | Photohawk",
    description:
      "Dispara conectado con CloudTether y cada JPEG va directo a tu galería de Photohawk mientras disparas, listo para la búsqueda por cara y dorsal y para vender.",
  },
  hero: {
    badge: "Con CloudTether",
    eyebrow: "En todos los planes de Photohawk",
    title: "Sube fotos en directo desde tu cámara",
    subtitle:
      "Conecta tu cámara a CloudTether en tu móvil y cada JPEG que hagas irá directo a tu galería de Photohawk. La gente puede encontrarse por cara o por dorsal y comprar mientras el evento sigue en marcha.",
    primary: "Consigue CloudTether",
    secondary: "Configúralo paso a paso",
    note: "CloudTether es un servicio aparte con su propia suscripción. Tu plan de Photohawk no cambia.",
  },
  steps: {
    heading: "Cómo funciona",
    subtitle: "Configúralo una vez y luego solo dispara.",
    items: [
      {
        id: "camera",
        title: "Conecta tu cámara",
        body: "Abre CloudTether en tu iPhone o móvil Android y conecta la cámara por cable o por Wi-Fi. Puedes añadir más de una.",
      },
      {
        id: "connect",
        title: "Conecta Photohawk una vez",
        body: "En CloudTether, ve a «Deliver», elige «Photohawk» e inicia sesión con tu correo y contraseña de Photohawk.",
      },
      {
        id: "choose",
        title: "Elige la galería",
        body: "Elige el espacio de trabajo, la galería y la colección a los que entrega la sesión, y toca «Use this gallery».",
      },
      {
        id: "shoot",
        title: "Dispara",
        body: "Cada JPEG llega a esa colección momentos después de hacerlo. Photohawk se encarga del resto: búsqueda, marcas de agua y ventas.",
      },
    ],
  },
  shots: {
    heading: "Así se ve",
    subtitle: "CloudTether en tu móvil, enviando a Photohawk.",
    items: [
      {
        id: "connect",
        caption: "Conecta Photohawk una vez, con tu correo y contraseña de Photohawk.",
        alt: "Pantalla Connect Photohawk de CloudTether, con un correo, una contraseña oculta y un botón Connect",
      },
      {
        id: "choose-gallery",
        caption: "Elige el espacio de trabajo, la galería y la colección de la sesión.",
        alt: "Pantalla Photohawk gallery de CloudTether con Workspace Northlight Event Photography, Gallery City Marathon 2026 y Collection Finish Line, y un botón Use this gallery",
      },
      {
        id: "three-cameras",
        caption: "Conecta varias cámaras a una sesión, por cable o por Wi-Fi.",
        alt: "Una sesión en directo de CloudTether enviando a Photohawk desde tres cámaras, una por USB y dos por Wi-Fi, con 21 437 hechas, 4 enviándose y 21 433 entregadas",
      },
      {
        id: "shoot-settings",
        caption: "Envía solo los JPEG, o solo las fotos que valores en la cámara.",
        alt: "Ajustes de la sesión en CloudTether: entrega a City Marathon 2026, Finish Line, en Photohawk, con opciones para solo JPEG, solo fotos valoradas en la cámara y enviar también vídeos",
      },
      {
        id: "delivered",
        caption: "Mira lo que está en camino y lo que ya ha llegado a Photohawk.",
        alt: "Una sesión de CloudTether, City Marathon 2026, con 21 433 fotos en Photohawk, 15 de 16 subiéndose y las últimas fotos en camino",
      },
      {
        id: "destinations",
        caption: "Envía a Photohawk y a tus otros servicios a la vez.",
        alt: "Pantalla Deliver to de CloudTether con Photohawk y Dropbox conectados y más servicios para añadir, como Google Drive y OneDrive",
      },
    ],
    heroAlt: "Una sesión en directo de CloudTether enviando a Photohawk: un corredor en la meta, con 21 437 hechas, 4 enviándose y 21 433 entregadas",
  },
  landing: {
    heading: "Qué pasa cuando llega una foto",
    body: "Photohawk procesa cada foto en sus propios servidores, así que CloudTether solo tiene que enviarla. Normalmente, en menos de un minuto desde que llega, cada foto:",
    points: [
      { id: "search", text: "se puede buscar por cara (búsqueda por selfie), por dorsal y por hora" },
      { id: "protect", text: "se muestra con tu marca de agua y la protección de vistas previas, nunca como el original" },
      { id: "sell", text: "está a la venta con los productos de tu galería y se entrega a los compradores automáticamente" },
    ],
  },
  shoot: {
    heading: "Hecho para una sesión intensa",
    body: "CloudTether sigue enviando mientras tú sigues disparando, y te muestra dónde está cada foto.",
    points: [
      { id: "cameras", text: "Conecta varias cámaras a una sesión, por USB y Wi-Fi a la vez. Los archivos de cada cámara se mantienen separados." },
      { id: "counts", text: "Recuentos en directo de lo que has disparado, lo que se está enviando y lo que ya se ha entregado a Photohawk." },
      { id: "pocket", text: "«Pocket mode» oscurece la pantalla mientras la conexión sigue funcionando, para que puedas guardar el móvil." },
      { id: "copies", text: "CloudTether guarda los originales en tu móvil 14 días después de la entrega, y «History» muestra todas tus sesiones anteriores." },
      { id: "more", text: "Envía a Photohawk y a otros servicios, como Dropbox o Google Drive, a la vez." },
    ],
  },
  files: {
    heading: "Qué se sube",
    subtitle: "Photohawk acepta fotos JPEG. Los ajustes de la sesión en CloudTether deciden qué sale de la cámara.",
    rows: [
      { id: "jpeg", label: "JPEG", value: "Se sube, conservando la hora de captura y otros datos." },
      { id: "rawjpeg", label: "RAW + JPEG", value: "Activa «JPEG only — leave RAW files on the camera» y solo se envía el JPEG." },
      { id: "raw", label: "Solo RAW", value: "No se sube a Photohawk. Cambia tu cámara a RAW + JPEG." },
      { id: "rated", label: "Fotos valoradas", value: "Activa «Only shots I rate in camera» para enviar solo las fotos que valores." },
      { id: "video", label: "Vídeo", value: "Con «Also send videos» activado, los clips llegan a Photohawk solo en el plan Max." },
    ],
  },
  roles: {
    heading: "Quién puede hacer qué",
    items: [
      { id: "galleries", text: "CloudTether muestra las galerías y colecciones que ya existen, así que créalas antes en Photohawk." },
      { id: "upload", text: "Los propietarios, administradores y miembros del equipo con el rol de fotógrafo pueden subir fotos." },
      { id: "restricted", text: "Si solo tienes acceso a ciertas galerías de un espacio de trabajo, CloudTether muestra solo esas." },
      { id: "sales", text: "Los vendedores no pueden subir fotos." },
    ],
  },
  trouble: {
    heading: "Si algo no va bien",
    items: [
      {
        id: "reconnect",
        title: "CloudTether te pide volver a conectar",
        body: "Tu inicio de sesión de Photohawk en CloudTether caduca de vez en cuando, por ejemplo después de restablecer tu contraseña. Toca «Reconnect Photohawk» y vuelve a iniciar sesión.",
      },
      {
        id: "missing",
        title: "Las fotos no aparecen",
        body: "Comprueba que tu cámara guarda JPEG o RAW + JPEG, y que la sesión entrega a la colección que estás mirando. Una foto puede tardar hasta un minuto en aparecer.",
      },
      {
        id: "gallery",
        title: "Tu galería no aparece",
        body: "Crea antes la galería y sus colecciones en Photohawk, y vuelve a elegirlas en CloudTether. Solo los propietarios y administradores pueden crear galerías.",
      },
      {
        id: "paused",
        title: "Las subidas se han pausado",
        body: "Si tu espacio de trabajo supera su almacenamiento sin tarjeta registrada, las nuevas subidas se pausan hasta que el propietario añada una tarjeta. No se borra nada.",
      },
    ],
  },
  faq: {
    heading: "Preguntas",
    items: [
      {
        q: "¿En qué planes de Photohawk funciona?",
        a: "En todos, para las fotos. Los vídeos llegan a Photohawk solo en el plan Max.",
      },
      {
        q: "¿CloudTether guarda mi contraseña de Photohawk?",
        a: "No. Inicias sesión una vez y CloudTether guarda un token de sesión, no tu contraseña. Cuando el token caduca, vuelves a iniciar sesión.",
      },
      {
        q: "¿Podemos disparar varias personas en la misma galería?",
        a: "Sí. Conecta varias cámaras a una sesión en CloudTether, o cada persona conecta su propia cuenta de Photohawk y elige la misma galería, o una colección cada una.",
      },
      {
        q: "¿Y si dos fotos tienen el mismo nombre de archivo?",
        a: "Una foto con el mismo nombre en la misma colección sustituye a la anterior. CloudTether mantiene separados los archivos de cada cámara, pero si varias personas disparan en una colección, dale a cada cámara su propio prefijo de nombre de archivo.",
      },
      {
        q: "¿Sigo necesitando el cargador web?",
        a: "Para las fotos que no hayas disparado conectado, sí. Las carpetas de una tarjeta o de un ordenador se suben más rápido con el cargador web.",
      },
    ],
  },
  cta: {
    heading: "¿Listo para disparar en directo?",
    body: "Consigue CloudTether, conecta tu cuenta de Photohawk una vez y tu próximo evento llenará tu galería mientras disparas.",
    primary: "Consigue CloudTether",
    secondary: "Lee la guía de configuración",
  },
};

export default es;
